import { Controller } from "@hotwired/stimulus"

// Unified error notification system
export default class extends Controller {
  static classes = ["container", "error", "warning", "success", "info"]
  static values = {
    autoDismiss: { type: Number, default: 5000 },
    maxNotifications: { type: Number, default: 3 }
  }

  connect() {
    this.notifications = new Map()
    this.retryActions = new Map()

    // Create container if it doesn't exist
    if (!document.getElementById('error-notifications')) {
      this.createContainer()
    }

    // Set up global error handlers
    this.setupGlobalHandlers()
  }

  createContainer() {
    const container = document.createElement('div')
    container.id = 'error-notifications'
    container.className = 'fixed top-4 right-4 z-50 space-y-3 max-w-md'
    document.body.appendChild(container)
  }

  // Show user-friendly error with context and actions
  showError(context, options = {}) {
    const {
      title = "Something went wrong",
      message = "Please try again",
      technical = null,
      actions = [],
      persistent = false,
      retryCallback = null,
      errorId = Date.now()
    } = options

    const notification = this.createNotification('error', {
      title,
      message,
      technical,
      actions,
      persistent,
      errorId
    })

    if (retryCallback) {
      this.retryActions.set(errorId, retryCallback)
    }

    this.showNotification(notification, errorId, persistent)
    return errorId
  }

  // Context-aware error messages
  showConnectionError(context, retryCallback = null) {
    return this.showError(context, {
      title: "Connection Lost",
      message: this.getContextualMessage(context),
      technical: "Network connection interrupted",
      actions: [
        { label: "Retry", action: 'retry', primary: true },
        { label: "Save Draft", action: 'saveDraft' },
        { label: "Dismiss", action: 'dismiss' }
      ],
      persistent: true,
      retryCallback
    })
  }

  showDataError(context, retryCallback = null) {
    return this.showError(context, {
      title: "Data Error",
      message: `Failed to ${context.operation}. Your data may not have been saved.`,
      technical: `${context.technical || 'Server error'}`,
      actions: [
        { label: "Try Again", action: 'retry', primary: true },
        { label: "Check Status", action: 'checkStatus' },
        { label: "Dismiss", action: 'dismiss' }
      ],
      retryCallback
    })
  }

  showValidationError(errors, context) {
    const message = Array.isArray(errors)
      ? errors.join(', ')
      : errors.toString()

    return this.showError(context, {
      title: "Please Fix These Issues",
      message,
      actions: [
        { label: "Review Form", action: 'focus' },
        { label: "Dismiss", action: 'dismiss' }
      ]
    })
  }

  getContextualMessage(context) {
    switch (context.operation) {
      case 'saving':
        return "Your changes may not have been saved. Please check and try again."
      case 'loading':
        return "Couldn't load the latest data. You may be seeing outdated information."
      case 'uploading':
        return "File upload was interrupted. Please try uploading again."
      case 'calculating':
        return "Price calculation failed. Numbers shown may be incorrect."
      default:
        return "The operation was interrupted. Please try again."
    }
  }

  createNotification(type, { title, message, technical, actions, errorId }) {
    const notification = document.createElement('div')
    notification.id = `notification-${errorId}`
    notification.className = this.getNotificationClasses(type)

    notification.innerHTML = `
      <div class="flex items-start">
        <div class="flex-shrink-0">
          ${this.getIcon(type)}
        </div>
        <div class="ml-3 flex-1">
          <h4 class="text-sm font-medium ${this.getTextColor(type)}">${title}</h4>
          <p class="mt-1 text-sm ${this.getSecondaryTextColor(type)}">${message}</p>
          ${technical ? `<details class="mt-2">
            <summary class="text-xs text-gray-500 cursor-pointer">Technical details</summary>
            <p class="text-xs text-gray-400 mt-1 font-mono">${technical}</p>
          </details>` : ''}
          ${actions.length > 0 ? this.createActionButtons(actions, errorId) : ''}
        </div>
        <div class="ml-4 flex-shrink-0">
          <button type="button" class="text-gray-400 hover:text-gray-500 focus:outline-none" 
                  data-action="click->error-notification#dismiss" 
                  data-error-id="${errorId}">
            <span class="sr-only">Close</span>
            <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
    `

    return notification
  }

  createActionButtons(actions, errorId) {
    const buttons = actions.map(action => {
      const buttonClass = action.primary
        ? "bg-red-600 hover:bg-red-700 text-white"
        : "bg-white hover:bg-gray-50 text-gray-900 border border-gray-300"

      return `<button type="button" class="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md ${buttonClass} focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 mr-2 mt-2"
                      data-action="click->error-notification#handleAction"
                      data-action-type="${action.action}"
                      data-error-id="${errorId}">
                ${action.label}
              </button>`
    }).join('')

    return `<div class="mt-3">${buttons}</div>`
  }

  handleAction(event) {
    const actionType = event.target.dataset.actionType
    const errorId = event.target.dataset.errorId

    switch (actionType) {
      case 'retry':
        this.retryOperation(errorId)
        break
      case 'saveDraft':
        this.saveDraft()
        break
      case 'checkStatus':
        this.checkStatus()
        break
      case 'focus':
        this.focusFirstError()
        break
      case 'dismiss':
        this.dismiss(event)
        break
    }
  }

  retryOperation(errorId) {
    const retryCallback = this.retryActions.get(errorId)
    if (retryCallback && typeof retryCallback === 'function') {
      this.dismiss({ target: { dataset: { errorId } } })
      retryCallback()
    }
  }

  saveDraft() {
    // Try to save current form state
    const forms = document.querySelectorAll('form[data-controller*="sales-form"]')
    forms.forEach(form => {
      const controller = this.application.getControllerForElementAndIdentifier(form, 'sales-form')
      if (controller && controller.saveDraft) {
        controller.saveDraft()
      }
    })
  }

  checkStatus() {
    // Refresh page data
    window.location.reload()
  }

  focusFirstError() {
    const errorField = document.querySelector('.field_with_errors input, .border-red-500')
    if (errorField) {
      errorField.focus()
      errorField.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  showNotification(notification, errorId, persistent = false) {
    const container = document.getElementById('error-notifications')

    // Limit number of notifications
    this.enforceNotificationLimit(container)

    container.appendChild(notification)
    this.notifications.set(errorId, notification)

    // Animate in
    setTimeout(() => {
      notification.classList.add('animate-slide-in')
    }, 10)

    // Auto-dismiss unless persistent
    if (!persistent) {
      setTimeout(() => {
        this.dismiss({ target: { dataset: { errorId } } })
      }, this.autoDismissValue)
    }
  }

  enforceNotificationLimit(container) {
    const notifications = container.children
    while (notifications.length >= this.maxNotificationsValue) {
      const oldest = notifications[0]
      this.fadeOutAndRemove(oldest)
    }
  }

  dismiss(event) {
    const errorId = event.target.dataset.errorId
    const notification = this.notifications.get(errorId)

    if (notification) {
      this.fadeOutAndRemove(notification)
      this.notifications.delete(errorId)
      this.retryActions.delete(errorId)
    }
  }

  fadeOutAndRemove(element) {
    element.classList.add('animate-slide-out')
    setTimeout(() => {
      element.remove()
    }, 300)
  }

  getNotificationClasses(type) {
    const baseClasses = "transform transition-all duration-300 ease-in-out max-w-md w-full shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden"

    switch (type) {
      case 'error':
        return `${baseClasses} bg-red-50 ring-red-200`
      case 'warning':
        return `${baseClasses} bg-yellow-50 ring-yellow-200`
      case 'success':
        return `${baseClasses} bg-green-50 ring-green-200`
      case 'info':
        return `${baseClasses} bg-blue-50 ring-blue-200`
      default:
        return `${baseClasses} bg-gray-50 ring-gray-200`
    }
  }

  getIcon(type) {
    switch (type) {
      case 'error':
        return `<svg class="h-6 w-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                </svg>`
      case 'warning':
        return `<svg class="h-6 w-6 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>`
      case 'success':
        return `<svg class="h-6 w-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>`
      default:
        return `<svg class="h-6 w-6 text-blue-400" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
                </svg>`
    }
  }

  getTextColor(type) {
    switch (type) {
      case 'error': return 'text-red-800'
      case 'warning': return 'text-yellow-800'
      case 'success': return 'text-green-800'
      default: return 'text-blue-800'
    }
  }

  getSecondaryTextColor(type) {
    switch (type) {
      case 'error': return 'text-red-700'
      case 'warning': return 'text-yellow-700'
      case 'success': return 'text-green-700'
      default: return 'text-blue-700'
    }
  }

  setupGlobalHandlers() {
    // Replace generic fetch error handling
    this.setupFetchInterceptor()

    // Handle WebSocket/ActionCable connection errors
    this.setupActionCableHandlers()

    // Handle form submission errors
    this.setupFormErrorHandlers()
  }

  setupFetchInterceptor() {
    const originalFetch = window.fetch
    const self = this

    window.fetch = function (...args) {
      return originalFetch.apply(this, args).catch(error => {
        // Only show user-facing errors, not hidden API calls
        if (!args[0].includes('/csp-violation-report')) {
          self.showConnectionError({
            operation: 'saving',
            url: args[0]
          })
        }
        throw error
      })
    }
  }

  setupActionCableHandlers() {
    // This would handle the "migration stream" type errors
    if (window.App && window.App.cable) {
      const originalConnect = window.App.cable.connect
      const self = this

      window.App.cable.connect = function () {
        const connection = originalConnect.call(this)

        connection.addEventListener('disconnect', () => {
          self.showConnectionError({
            operation: 'loading',
            technical: 'Real-time connection lost'
          }, () => {
            window.location.reload()
          })
        })

        return connection
      }
    }
  }

  setupFormErrorHandlers() {
    document.addEventListener('turbo:frame-missing', (event) => {
      this.showError({}, {
        title: "Page Content Missing",
        message: "Some content couldn't be loaded. Please refresh the page.",
        actions: [
          { label: "Refresh Page", action: 'checkStatus', primary: true },
          { label: "Dismiss", action: 'dismiss' }
        ]
      })
    })
  }
}
